import React, { useEffect, useRef, useState } from 'react';
import { useAuth, UserProfile } from '@clerk/clerk-react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getApiBase } from '../../utils/apiBase';
import { accountModerationCopy, observeAccountStatus } from '../../utils/accountModeration';

export function AccountModeration({ children }) {
  const { userId, getToken, signOut } = useAuth();
  const { i18n } = useTranslation();
  const copy = accountModerationCopy(i18n.resolvedLanguage || i18n.language);
  const [snapshot, setSnapshot] = useState(null);
  const [manage, setManage] = useState(false), [error, setError] = useState(false), [busy, setBusy] = useState(false);
  const observer = useRef(null), token = useRef(getToken), lock = useRef(false), identity = useRef(userId);
  token.current = getToken; identity.current = userId;
  useEffect(() => {
    setManage(false); setError(false); setBusy(false); lock.current = false;
    if (!userId) return;
    const current = observeAccountStatus(async signal => {
      const jwt = await token.current();
      if (!jwt || signal.aborted) throw new Error('auth_required');
      const response = await fetch(`${getApiBase()}/moderation/account-status`, { headers: { Authorization: `Bearer ${jwt}` }, cache: 'no-store', signal });
      if (!response.ok) throw new Error('account_status_unavailable');
      return response.json();
    }, value => { setSnapshot({ userId, value }); setError(false); }, () => setError(true));
    observer.current = current;
    const foreground = () => { if (!document.hidden) void current.refresh(); };
    foreground(); const timer = setInterval(foreground, 60000);
    window.addEventListener('focus', foreground); document.addEventListener('visibilitychange', foreground);
    return () => { current.stop(); clearInterval(timer); window.removeEventListener('focus', foreground); document.removeEventListener('visibilitychange', foreground); };
  }, [userId]);
  const state = userId && snapshot?.userId === userId ? snapshot.value : null;
  const restricted = state?.enabled && state.status !== 'ACTIVE';
  const warning = state?.enabled ? state.warnings[0] : null;
  async function perform(action) {
    if (lock.current) return;
    const actor = userId; lock.current = true; setBusy(true); setError(false);
    try { await action(); }
    catch { if (identity.current === actor) setError(true); }
    finally { if (identity.current === actor) { lock.current = false; setBusy(false); } }
  }
  const acknowledge = () => perform(async () => {
    const jwt = await getToken();
    if (!jwt || identity.current !== userId) throw new Error('auth_required');
    const response = await fetch(`${getApiBase()}/moderation/warnings/${warning.id}/ack`, {
      method: 'POST', headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' }, body: '{}',
    });
    if (!response.ok) throw new Error('ack_failed');
    if (identity.current === userId) await observer.current?.refresh(true);
  });
  if (restricted) return <Box component="main" sx={{ minHeight: '100dvh', background: '#0F1014', color: '#fff', p: 3 }}>
    <Box sx={{ maxWidth: 640, mx: 'auto', pt: 5, display: 'grid', gap: 2 }}>
      <Typography component="h1" variant="h5">{state.status === 'BANNED' ? copy.banned : copy.suspended}</Typography>
      <Typography>{copy.explanation}</Typography>
      {state.until && <Typography>{copy.until} {new Date(state.until).toLocaleString(i18n.language)}</Typography>}
      {warning && <Alert severity="warning"><Typography>{copy.warning}</Typography>{warning.message}<Button disabled={busy} onClick={acknowledge}>{copy.acknowledge}</Button></Alert>}
      {error && <Alert severity="error">{copy.error}</Alert>}
      <Button href="mailto:contact@qubiq.games">{copy.contact}</Button>
      <Button onClick={() => setManage(!manage)}>{manage ? copy.back : copy.account}</Button>
      {manage && <UserProfile routing="virtual" apiKeysProps={{ hide: true }} />}
      <Button disabled={busy} onClick={() => perform(() => signOut({ redirectUrl: '/' }))}>{copy.signout}</Button>
      <Button disabled={busy} onClick={() => observer.current?.refresh()}>{copy.retry}</Button>
    </Box>
  </Box>;
  return <>{children}<Dialog open={Boolean(warning)} PaperProps={{ sx: { bgcolor: '#18181C', color: '#fff' } }}>
    <DialogTitle>{copy.warning}</DialogTitle><DialogContent><Typography sx={{ whiteSpace: 'pre-wrap' }}>{warning?.message}</Typography>
      <Button href="mailto:contact@qubiq.games">{copy.contact}</Button>{error && <Alert severity="error">{copy.error}</Alert>}
    </DialogContent><DialogActions><Button disabled={busy} onClick={acknowledge}>{copy.acknowledge}</Button></DialogActions>
  </Dialog></>;
}
